/**
 * English UI strings. The source of truth for keys: `fr.ts` must provide exactly the same set (the
 * `Dict` type enforces it at compile time, tests/unit/i18n.test.ts at test time).
 * Plurals use `key_one` / `key_other`; `\n` in a value is a forced line break in headlines.
 */
export const en = {
  // ---- common ----
  'common.back': '← Back',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.close': 'Close',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.remove': 'Remove',
  'common.change': 'Change',
  'common.keep': 'Keep',
  'common.paste': 'Paste',
  'common.add': '+ Add',
  'common.other': 'Other',
  'common.tryAgain': 'Try again',
  'common.on': 'On',
  'common.off': 'Off',
  'common.settings': 'Settings',
  'common.loading': 'Loading…',

  // ---- sign up / log in ----
  'auth.title': 'Welcome\nto Tally.',
  'auth.lead': 'Tally turns a sentence, a voice note or a receipt into expenses. Your Gemini key stays on your device.',
  'auth.note': 'Sign in with your Google account. Tally only learns your email address.',
  'auth.google': 'Continue with Google',
  'auth.privacy': 'Privacy policy',
  'auth.err.cancelled': 'Sign-in was cancelled. Try again whenever you like.',
  'auth.err.failed': 'Google could not sign you in. Try again.',
  'auth.err.signups_disabled': 'Sign-ups are closed on this Tally.',
  'auth.err.offline': "You're offline.",
  // ---- privacy ----
  'privacy.title': 'Privacy',
  'privacy.lead': 'What Tally keeps, and where.',
  'privacy.account': 'Your account',
  'privacy.accountText': 'You sign in with Google. Tally stores the stable id and the email address of your Google account, nothing else from it. Your Google password never touches Tally.',
  'privacy.entries': 'Your entries',
  'privacy.entriesText': 'Expenses, categories, settings and the devices you enabled notifications on are stored in Tally’s database, for your account only. They are used to show your ledger and to send the notifications you turned on.',
  'privacy.gemini': 'Gemini',
  'privacy.geminiText': 'Text, voice notes and receipt photos go from your browser straight to Google’s Gemini API with your own key. The key stays on your device; Tally’s server never sees it, nor the audio or the photos. Only the entries you confirm are saved.',
  'privacy.cookies': 'Cookies',
  'privacy.cookiesText': 'One cookie keeps you signed in for 30 days; another lives for ten minutes while you sign in. There are no analytics, no third-party scripts and no advertising.',
  'privacy.push': 'Push notifications',
  'privacy.pushText': 'If you turn them on, the subscription your browser creates is stored and every message is encrypted end to end. Remove a device or turn notifications off at any time in Settings.',
  'privacy.yours': 'Your data is yours',
  'privacy.yoursText': 'Export everything as CSV from the overview. Deleting your account in Settings removes all of it at once. The source code is public.',

  // ---- setup ----
  'setup.step': 'Step {n} / {total}',
  'setup.keyTitle': 'Bring your\nown key.',
  'setup.keyLead': 'Tally uses Gemini to read what you type, say, or photograph. Paste a key from Google AI Studio. The free tier is enough.',
  'setup.keyLabel': 'Google AI Studio API key',
  'setup.keyPlaceholder': 'Paste your key',
  'setup.getKey': 'Get a key at aistudio.google.com →',
  'setup.keyNote': 'Your key stays on this device and is only sent to Google.',
  'setup.continue': 'Continue',
  'setup.defaultsTitle': 'A few\ndefaults.',
  'setup.currency': 'Currency',
  'setup.language': 'Language',
  'setup.budget': 'Budget',
  'setup.perMonth': '/ month',
  'setup.noBudget': 'None',
  'setup.categoriesHint': 'Categories — tap to remove',
  'setup.newCategory': 'New category',
  'setup.start': 'Start logging',
  'setup.saveFailed': 'Could not save. Try again.',
  'lang.auto': 'Auto (EN / FR)',
  'lang.en': 'English',
  'lang.fr': 'Français',

  // ---- Gemini key check ----
  'key.checking': 'Checking key…',
  'key.ok': 'Key works · {model}',
  'key.invalid_key': 'Key rejected by Google',
  'key.model_not_found': 'Model not found: {model}',
  'key.network': 'No network',
  'key.quota': 'Too many requests. Try again in a minute.',
  'key.other': 'Could not check the key',

  // ---- home ----
  'home.spent': 'Spent this month',
  'home.ofBudget': '{pct}% of {budget}',
  'home.daysLeft_one': '{count} day left',
  'home.daysLeft_other': '{count} days left',
  'home.lastDay': 'Last day',
  'home.today': 'Today',
  'home.yesterday': 'Yesterday',
  'home.empty': 'Nothing logged yet. Type a line or tap the mic.',
  'home.showMonth': 'Show {month} →',
  'home.monthEmpty': 'Nothing logged.',
  'home.loadFailed': 'Could not load your entries.',

  // ---- composer ----
  'composer.label': 'Describe an expense',
  'composer.placeholder': 'Coffee 4.50 — or just talk',
  'composer.camera': 'Snap or pick a receipt',
  'composer.mic': 'Record a voice note',
  'composer.send': 'Send',

  // ---- capture sheet ----
  'capture.sheet': 'New expense',
  'capture.listening': 'Listening · {time}',
  'capture.releaseToSend': 'Release to send',
  'capture.tapToStop': 'Tap to stop',
  'capture.stopSend': 'Stop & send',
  'capture.thinkingListen': 'Gemini is listening…',
  'capture.thinkingRead': 'Gemini is reading…',
  'capture.voiceNote': 'Voice note · {time}',
  'capture.photo': 'Receipt photo',
  'capture.youWrote': 'You wrote',
  'capture.youSaid': 'You said',
  'capture.quote': '“{text}”',
  'capture.parsed': 'Gemini parsed',
  'capture.ai': 'AI',
  'capture.gemini': 'Gemini',
  'capture.amount': 'Amount',
  'capture.what': 'What',
  'capture.category': 'Category',
  'capture.when': 'When',
  'capture.note': 'Note',
  'capture.optional': 'Optional',
  'capture.found_one': '{count} entry found',
  'capture.found_other': '{count} entries found',
  'capture.total': 'Total',
  'capture.log_one': 'Log {count} entry',
  'capture.log_other': 'Log {count} entries',
  'capture.include': 'Include {name}',
  'capture.editRow': 'Edit {name}',
  'capture.typeIt': 'Type it',
  'capture.noReply': 'Gemini found no expense in that.',
  'capture.micDenied': 'Tally needs the microphone. Allow it in your browser settings.',
  'capture.micError': 'The microphone could not start.',
  'capture.tooShort': 'That was too short to hear anything.',
  'capture.saveFailed': 'Could not save. Try again.',
  'capture.invalidAmount': 'Check the amount.',
  'capture.invalidWhat': 'Say what it was.',
  'capture.invalidWhen': 'Pick a date and time.',
  'when.today': 'Today, {time}',
  'when.yesterday': 'Yesterday, {time}',
  'when.day': '{day}, {time}',

  // ---- Gemini errors ----
  'gemini.invalid_key': 'Gemini rejected your key. Check it in Settings.',
  'gemini.quota': "Gemini's free tier is out of requests for now. Try again in a minute.",
  'gemini.network': 'No connection to Gemini.',
  'gemini.bad_response': 'Gemini answered something I could not read.',
  'gemini.model_not_found': 'Gemini does not know the model “{model}”. Check it in Settings.',
  'gemini.unknown': 'Gemini could not answer. Try again.',
  'gemini.openSettings': 'Open Settings →',

  // ---- entry sheet ----
  'entry.title': 'Edit entry',
  'entry.delete': 'Delete entry',
  'entry.confirmDelete': 'Delete this entry?',
  'entry.deleted': 'Entry deleted',
  'entry.undo': 'Undo',
  'entry.saveFailed': 'Could not save. Try again.',
  'entry.deleteFailed': 'Could not delete. Try again.',

  // ---- views (ledger | analytics) ----
  'nav.views': 'Views',
  'nav.ledger': 'Ledger',
  'nav.analytics': 'Analytics',

  // ---- overview ----
  'overview.week': 'Week',
  'overview.month': 'Month',
  'overview.year': 'Year',
  'overview.periods': 'Period',
  'overview.prev': 'Previous period',
  'overview.next': 'Next period',
  'overview.weekLabel': 'Week {week} · {range}',
  'overview.stats_one': '{count} entry · avg {avg} / day',
  'overview.stats_other': '{count} entries · avg {avg} / day',
  'overview.statsYear': 'Avg {avg} / month',
  'overview.statsAhead_one': '{count} upcoming entry',
  'overview.statsAhead_other': '{count} upcoming entries',
  'overview.delta': '{delta}%',
  'overview.deltaLabel': '{delta}% vs {period}',
  'overview.compared': 'Change vs {period}',
  'overview.export': 'Export CSV →',
  'overview.empty': 'Nothing logged in this period.',
  'overview.loadFailed': 'Could not load this period.',
  'overview.askLabel': 'Ask your data',
  'overview.askPlaceholder': 'Ask: "how much on coffee this month?"',
  'overview.ask': 'Ask',
  'overview.askNoKey': 'Add your Gemini key in Settings to ask questions.',
  'overview.askThinking': 'Gemini is thinking',
  'overview.share': '{share}%',
  'overview.shareLabel': '{share}% of the total',
  'overview.openCategory': '{name}: entries and trend',
  'overview.report': '{month} report →',
  'overview.split': 'Fixed {fixed} · Day-to-day {daily}',
  'overview.uncategorised_one': '{count} entry has no category · Sort it →',
  'overview.uncategorised_other': '{count} entries have no category · Sort them →',
  'overview.budgetMark': 'Budget {budget}',

  // ---- category (analytics drill-down) ----
  'category.ofPeriod': '{share}% of {period}',
  'category.entries_one': '{count} entry',
  'category.entries_other': '{count} entries',
  'category.trend': 'Last 6 months',
  'category.loadFailed': 'Could not load these entries.',
  'category.avg': 'avg {amount}',
  'category.budget': 'Monthly budget',
  'category.noBudget': 'None',
  'category.setBudget': 'Set',
  'category.budgetPlaceholder': 'e.g. 250',
  'category.left': '{amount} left',
  'category.over': '{amount} over',
  'category.fixed': 'Fixed cost',
  'category.fixedHint': 'Rent, bills, subscriptions: counted apart from day-to-day spending.',
  'category.otherHint': 'Tap an entry to give it a category.',

  // ---- monthly report ----
  'report.title': 'Report',
  'report.label': 'Monthly report',
  'report.inProgress': 'Month in progress',
  'report.prev': 'Previous month',
  'report.next': 'Next month',
  'report.ofBudget': '{pct}% of the {budget} budget',
  'report.vsPrev': '{delta}% vs {month} ({diff})',
  'report.noPrev': 'Nothing logged in {month} to compare with.',
  'report.entries': 'Entries',
  'report.avgDay': 'Avg / day',
  'report.busiest': 'Busiest day',
  'report.byCategory': 'By category',
  'report.biggest': 'Biggest expenses',
  'report.history': 'Last 6 months',
  'report.pdf': 'Save as PDF',
  'report.empty': 'Nothing logged in {month}.',
  'report.loadFailed': 'Could not load this report.',
  'report.summarize': 'Summarize with Gemini',
  'report.summaryThinking': 'Gemini is writing',
  'report.summaryLabel': 'Summary',

  // ---- settings ----
  'settings.title': 'Settings',
  'settings.gemini': 'Gemini',
  'settings.apiKey': 'API key',
  'settings.noKey': 'Not set',
  'settings.model': 'Model',
  'settings.keyStored': 'Your key is stored only in this browser.',
  'settings.saveKey': 'Save key',
  'settings.defaults': 'Defaults',
  'settings.categories': 'Categories',
  'settings.removeCategory': 'Remove {name}? Entries keep their history as Other.',
  'settings.notifications': 'Notifications',
  'settings.notifDevice': 'Notifications on this device',
  'settings.notifUnsupported': 'This browser cannot receive notifications.',
  'settings.notifIos': 'On iPhone, add Tally to your Home Screen first, then enable notifications.',
  'settings.notifIosHow': 'In Safari, tap Share, then “Add to Home Screen”, and open Tally from your Home Screen.',
  'settings.notifDenied': 'Notifications are blocked for Tally. Allow them in your browser settings.',
  'settings.notifFailed': 'Could not turn on notifications.',
  'settings.reminder': 'Daily reminder',
  'settings.reminderTime': 'Reminder time',
  'settings.onlyIfEmpty': 'Only if nothing was logged',
  'settings.budgetAlerts': 'Budget alerts',
  'settings.budgetAlertsHint': 'At 50%, 80% and 100% of your budget',
  'settings.weekly': 'Weekly summary',
  'settings.weeklyHint': 'Monday morning',
  'settings.monthly': 'Monthly report',
  'settings.monthlyHint': 'First day of the month',
  'settings.test': 'Send a test notification',
  'settings.testSent_one': 'Sent to {count} device.',
  'settings.testSent_other': 'Sent to {count} devices.',
  'settings.testNone': 'No device received it.',
  'settings.devices': 'Devices',
  'settings.thisDevice': 'This device',
  'settings.removeDevice': 'Remove {name}',
  'settings.deviceSeen': 'Last seen {date}',
  'settings.unknownDevice': 'Unknown device',
  'settings.deviceOn': '{browser} on {os}',
  'settings.account': 'Account',
  'settings.email': 'Email',
  'settings.signIn': 'Sign-in',
  'settings.signInGoogle': 'With Google',
  'settings.logout': 'Log out',
  'settings.deleteAccount': 'Delete account',
  'settings.deleteWarning': 'This deletes your account and all your entries. Type your email to confirm.',
  'settings.deleteForever': 'Delete forever',
  'settings.install': 'Install',
  'settings.installButton': 'Install Tally',
  'settings.installIos': 'In Safari, tap Share, then “Add to Home Screen”.',
  'settings.installOther': "Open your browser's menu and choose Install, or Add to Home Screen.",
  'settings.about': 'About',
  'settings.version': 'Tally v{version}',
  'settings.source': 'Source code on GitHub →',
  'settings.privacy': 'Privacy policy →',
  'settings.saved': 'Saved.',
  'settings.saveFailed': 'Could not save. Try again.',

  // ---- recurring bills ----
  'bills.title': 'Bills',
  'bills.billTitle': 'Bill',
  'bills.repeats': 'Repeats',
  'bills.firstDue': 'First due',
  'bills.paid': 'Paid',
  'bills.whenDue': 'When it comes due',
  'bills.mode.auto': 'Log it for me',
  'bills.mode.ask': 'Ask me first',
  'bills.mode.autoHint': 'Tally logs it on the day. Good for card payments that never change.',
  'bills.mode.askHint': 'Tally asks on the day, and you tap Paid it.',
  'bills.cadence.weekly': 'Weekly',
  'bills.cadence.monthly': 'Monthly',
  'bills.cadence.quarterly': 'Every 3 months',
  'bills.cadence.yearly': 'Yearly',
  'bills.once': 'Just once',
  'bills.on.weekly': 'Weekly, on {weekday}',
  'bills.on.monthly': 'Monthly, on the {nth}',
  'bills.on.quarterly': 'Every 3 months, on the {nth}',
  'bills.on.yearly': 'Yearly, on {date}',
  'bills.saveBill': 'Save bill',
  'bills.saved': '{name} saved · first on {date}',
  'bills.savedPaid': '{name} logged · next on {date}',
  'bills.savedMany_one': '{count} bill saved',
  'bills.savedMany_other': '{count} bills saved',
  'bills.logged': 'Logged · next one {date}',
  'bills.dueToday': 'Due today',
  'bills.overdue': 'Due since {date}',
  'bills.notYet': 'Not yet',
  'bills.paidIt': 'Paid it',
  'bills.nextToday': 'today',
  'bills.nextTomorrow': 'tomorrow',
  'bills.next': 'Next bill',
  'bills.nextMore_one': 'Next bill · {count} more this month',
  'bills.nextMore_other': 'Next bill · {count} more this month',
  'bills.logsItself': 'Logs itself',
  'bills.looksLike': 'Looks like a bill',
  'bills.seenSame': '{days}, same amount',
  'bills.notABill': 'Not a bill',
  'bills.keep': 'Make it a bill',
  'bills.prevMonth': 'Previous month',
  'bills.nextMonth': 'Next month',
  'bills.paidSum': 'Paid',
  'bills.toPay': 'Still to pay',
  'bills.found_one': '{count} found',
  'bills.found_other': '{count} found',
  'bills.nothingDue': 'Nothing due',
  'bills.addHint': 'Say a bill to put it on this day.',
  'bills.empty': 'No bills yet. Say one, like “Rent 1250 every month on the 1st”.',
  'bills.loadFailed': 'Could not load your bills.',
  'bills.open': 'Open {name}',
  'bills.day': '{date}: {amount}',
  'bills.dayEmpty': '{date}: nothing due',
  'bills.next3': 'Next',
  'bills.inDays_one': 'in {count} day',
  'bills.inDays_other': 'in {count} days',
  'bills.history': 'Paid through Tally',
  'bills.noHistory': 'Nothing paid through Tally yet.',
  'bills.skip': 'Skip this one',
  'bills.payNow': 'Mark paid now',
  'bills.stop': 'Stop this bill',
  'bills.confirmStop': 'Stop {name}? What it already paid stays in your ledger.',
  'bills.stopped': 'Bill stopped',
  'bills.failed': 'Could not do that. Try again.',
  'bills.since': 'Since {date}',
  'bills.perMonth': 'About {amount} a month',
  'bills.notFound': 'This bill no longer exists.',
  'bills.editTitle': 'Edit bill',
  'bills.spotted': 'Spotted in your entries',
  'bills.nextDue': 'Next due',
  'bills.foundTitle': 'These look like bills',
  'bills.foundLead': 'Same thing, same price, on a steady beat. Nothing becomes a bill until you keep it.',
  'bills.noneFound': 'Nothing to review.',
  'bills.fromBill': 'From a bill',
  'composer.billPlaceholder': 'A bill on {date} — or just talk',
  'nav.bills': 'Bills',

  // ---- toasts ----
  'toast.offline': "You're offline.",
  'toast.updateReady': 'Update ready',
  'toast.reload': 'Reload',
  'toast.install': 'Add Tally to your Home Screen',
  'toast.installAction': 'Install',
  'toast.error': 'Something went wrong.',
} as const;

export type DictKey = keyof typeof en;
export type Dict = { readonly [K in DictKey]: string };
